import { DeferredPromise } from '@cf-shared/shared-util';

/**
 * #4502 — what a singleton read should do when the database-recovery machinery takes its
 * failure.
 *
 * `DatabaseRecoveryService.interceptDatabaseError` answers `true` to mean "I have taken this failure".
 * Nearly every branch behind that answer reloads the page, so the caller freezing is right.
 * One branch does not: a graceful recovery reopens the SAME stored database in place and the
 * app is built to carry on. A caller frozen on a promise nobody holds cannot be woken, so the
 * database heals and the caller stays frozen.
 *
 * This module holds that decision as a plain function with its dependencies passed in, for the
 * same reason `database-wrapper-passthrough.ts` exists: `DatabaseWrapper` itself cannot be
 * loaded by the Node test runner, and the behaviour worth proving here is whether a promise
 * settles at all — which no source-text test can see.
 */

/**
 * How the database recovery a caller was told to wait for ended.
 *
 * This used to be a boolean, and `false` meant two things at once (df-4ebecd016a94). Most
 * callers are right not to care: they stop either way. The cold start is the exception,
 * because the two halves of that `false` differ in the one thing it needs to know — whether
 * anything is coming to finish the boot it just gave up on.
 */
export enum DatabaseRecoverySettlement {
  /** The stored database was repaired in place and the app is carrying on, so a caller may
   *  read again. */
  RepairedInPlace = 'repaired_in_place',
  /** Stop, and leave the boot screen alone: the page is going away, or the databases are being
   *  destroyed on purpose and the caller must not act on one of them.
   *
   *  Reading a database that is going away is pointless whichever of those it is, which is why
   *  they share one answer. The wording is loose in exactly one case, and deliberately: the
   *  test-fixture restore destroys databases on purpose without any reload following. That is
   *  the same conflation `SingletonDefaultReason.PageIsBeingReplaced` already makes, for the
   *  same reason — no caller has ever needed the two apart. */
  PageIsBeingReplaced = 'page_is_being_replaced',
  /** Nothing repaired anything and nothing is going to replace the page either.
   *
   *  One arm of `interceptDatabaseError` answers this: a call on a store handle the app closed
   *  itself, which says something about our own lifecycle rather than about the device, so it
   *  deliberately runs no recovery and triggers no reload. A caller that merely stops here is
   *  waiting for something that will never arrive — which is what left the cold start's loading
   *  splash up with nothing coming to take it down (df-4ebecd016a94). */
  NoRepairAndNoReload = 'no_repair_and_no_reload',
}

/**
 * The outcome of the database recovery currently in flight, as something a waiting caller can
 * actually be woken by. See `DatabaseRecoverySettlement` for what each answer means.
 *
 * It starts already settled `PageIsBeingReplaced`. That is the cautious answer — stop, and do
 * not touch the boot screen — so a path that answers "handled" without settling anything
 * itself gets the same treatment it has always had, rather than the newer
 * `NoRepairAndNoReload` behaviour it has never been judged against.
 *
 * `begin()` replaces it with a fresh unsettled promise whenever a recovery STARTS, and an
 * answer, once given, stands until the next recovery begins. That second part matters: after a
 * graceful repair succeeds, the recovery code deliberately keeps its in-recovery flag up for
 * another five seconds to absorb stale errors from the old handle, and during that window
 * `interceptDatabaseError` answers "handled" through its early returns with nothing in flight.
 * Keeping the settled `RepairedInPlace` answer means a caller intercepted in that window is
 * told at once that the database is healthy — which it is. The two `settleAndStandDown` callers
 * are the only things that throw a standing answer away rather than waiting for the next
 * recovery, and that method's comment says why.
 */
export class DatabaseRecoveryOutcome {
  private settled: DeferredPromise<DatabaseRecoverySettlement> =
    DatabaseRecoveryOutcome.alreadySettled(DatabaseRecoverySettlement.PageIsBeingReplaced);

  /** a recovery is starting: callers that wait from now on join THIS one, rather than reading
   *  the answer the previous recovery left behind */
  begin() { this.settled = DeferredPromise.defer<DatabaseRecoverySettlement>(); }

  /** the database was repaired in place — waiting callers should read again */
  repaired() { this.settled.resolve(DatabaseRecoverySettlement.RepairedInPlace); }

  /** the page is being replaced — waiting callers should give up quietly */
  pageIsBeingReplaced() { this.settled.resolve(DatabaseRecoverySettlement.PageIsBeingReplaced); }

  /** No recovery is going to run at all, because the databases are being destroyed ON PURPOSE
   *  (an account wipe, a sign-out, a test-fixture restore). Waiting callers must give up, and
   *  they are told the same thing a reload tells them: stop, and leave the boot screen alone. */
  databasesAreBeingDestroyedOnPurpose() {
    this.settleAndStandDown(DatabaseRecoverySettlement.PageIsBeingReplaced);
  }

  /** No recovery is going to run AND no reload is coming — the app called a store handle it had
   *  already closed itself. Waiting callers must give up, and a caller whose own job is
   *  unfinished has to finish it some other way, because nothing else is going to
   *  (df-4ebecd016a94). */
  noRepairAndNoReloadIsComing() {
    this.settleAndStandDown(DatabaseRecoverySettlement.NoRepairAndNoReload);
  }

  /** Settle for callers already waiting, then stand down so later callers get the same answer
   * rather than an older recovery's.
   *
   * It does BOTH halves, and each covers a case the other cannot:
   *
   * - It settles the standing promise, which wakes anyone already parked on it. A recovery
   *   really can be in flight here: the destruction check runs BEFORE the "a recovery is
   *   already starting" guard, so a caller can be waiting on an unsettled promise at this
   *   moment. Replacing that promise without settling it would strand that caller for ever —
   *   the freeze this whole module exists to remove, arriving by a new route. A sign-out
   *   would hide it (the page reloads), but a fixture restore does not reload, and reading
   *   singletons is the normal next thing after one.
   * - It then replaces the promise, which is what handles the opposite case: an earlier repair
   *   in the same page session leaves a settled `RepairedInPlace` standing for ever by design,
   *   and settling that again is a no-op, so a caller would be told the database is healthy
   *   while it is being deleted.
   *
   * Both callers need both halves, which is why they share this rather than each writing the
   * pair out. Writing one half and forgetting the other is silent in both directions. */
  private settleAndStandDown(settlement: DatabaseRecoverySettlement) {
    this.settled.resolve(settlement);
    this.settled = DatabaseRecoveryOutcome.alreadySettled(settlement);
  }

  /** never hangs: that is the whole point, because callers used to be handed a promise nobody
   *  held a reference to and so could never be woken at all */
  whenSettled(): Promise<DatabaseRecoverySettlement> { return this.settled; }

  private static alreadySettled(
    value: DatabaseRecoverySettlement,
  ): DeferredPromise<DatabaseRecoverySettlement> {
    // DeferredPromise.defer() then resolved at once, rather than Promise.resolve(), so the
    // field keeps one type and `resolve` is always callable on it
    const promise = DeferredPromise.defer<DatabaseRecoverySettlement>();
    promise.resolve(value);
    return promise;
  }
}

/** Why the caller was told to fall back to its default document. The two cases want opposite
 * things from telemetry, which is the whole reason they are told apart:
 *
 * - `PageIsBeingReplaced` — the recovery is reloading the page, or nothing was being repaired at
 *   all. Reporting this would be noise: the page is going away, and the reload path already has
 *   its own telemetry.
 * - `StillUnreadableAfterRepair` — the database was repaired and the read STILL failed. Nothing
 *   else records that, and it means the app carries on with default documents, so it is exactly
 *   the fact a future debugger needs. */
export enum SingletonDefaultReason {
  PageIsBeingReplaced = 'page_is_being_replaced',
  StillUnreadableAfterRepair = 'still_unreadable_after_repair',
}

/** What the caller should do with the read. */
export type SingletonReadOutcome<T> =
  /** the read succeeded — use this document */
  | { kind: 'doc'; doc: T }
  /** stop trying to read; fall back to the caller's default document */
  | { kind: 'useDefault'; reason: SingletonDefaultReason }
  /** not a recovery case — the caller applies its own rules to this error */
  | { kind: 'error'; err: unknown };

export interface SingletonReadDeps<T> {
  /** perform the read once */
  read: () => Promise<T>;
  /** offer the error to the recovery machinery; true means it took the failure */
  interceptDatabaseError: (err: unknown) => boolean;
  /** wait for that recovery; only `RepairedInPlace` makes a retry worthwhile */
  whenRecoverySettled: () => Promise<DatabaseRecoverySettlement>;
}

export async function readSingletonThroughRecoveryAsync<T>(
  deps: SingletonReadDeps<T>,
): Promise<SingletonReadOutcome<T>> {
  // at most two attempts, counted right here. Do NOT lean on the recovery machinery's own
  // "it failed again too soon" rule to stop a loop: for the first five seconds after a
  // graceful repair succeeds, a repeating error is answered by an early return that neither
  // starts another repair nor reloads, so an unbounded retry would spin hot until that
  // settling window closes.
  for (let attempt = 1; attempt <= 2; attempt++) {
    try {
      return { kind: 'doc', doc: await deps.read() };
    } catch (err) {
      // not a recovery case — the caller's own rules apply (document missing, a transient
      // network failure, or an error worth rethrowing)
      if (!deps.interceptDatabaseError(err)) {
        return { kind: 'error', err };
      }
      // the recovery took the failure. wait for it rather than freezing here for ever:
      // freezing is right only when the page is about to be replaced.
      const settlement = await deps.whenRecoverySettled();
      // Anything other than a repair means reading again would be pointless, so give up quietly
      // and let the caller use its default document. Every non-repair answer is treated alike ON
      // PURPOSE (df-4ebecd016a94): a handle the app closed itself will not answer a second read
      // any more than a database that is going away will. Only the cold start needs those apart,
      // because only the cold start still owes the user something afterwards.
      if (settlement !== DatabaseRecoverySettlement.RepairedInPlace) {
        return { kind: 'useDefault', reason: SingletonDefaultReason.PageIsBeingReplaced };
      }
      // repaired in place: the SAME stored database is open again, so a second read normally
      // returns the real document. if this was already the second attempt, stop.
    }
  }
  // both attempts failed and the recovery machinery took both failures. The database was
  // repaired between them and the read STILL did not work, which is worth reporting — the
  // caller does that; see SingletonDefaultReason.
  return { kind: 'useDefault', reason: SingletonDefaultReason.StillUnreadableAfterRepair };
}
